import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import apiClient from '../api/client'
import { useAuth } from './useAuth'
import { MeContext, type MeState } from './contexts'

interface MeResponse {
  email: string
  role: string
  available: boolean
  rotation_position: number | null
  rotation_size: number | null
}

export function MeProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isAsesor } = useAuth()
  const [available, setAvailableState] = useState(true)
  const [rotationPosition, setRotationPosition] = useState<number | null>(null)
  const [rotationSize, setRotationSize] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return
    setLoading(true)
    try {
      const res = await apiClient.get<MeResponse>('/me')
      setAvailableState(res.data.available)
      setRotationPosition(res.data.rotation_position)
      setRotationSize(res.data.rotation_size)
      setError(null)
    } catch {
      // Silencio: el portal funciona sin este dato. El pill se muestra con el
      // último valor conocido en vez de bloquear la pantalla por una caída
      // puntual del endpoint.
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  useEffect(() => { void refresh() }, [refresh])

  // La posición en la rotación depende de la carga de TODO el equipo, así que
  // cambia sin que este usuario haga nada.
  //
  // Solo se pollea para el ASESOR y cada 60 s, no cada 30 como el badge:
  // `GET /me` calcula la rotación con una query de 500 items más un get_item
  // por asesor, y el número que devuelve solo se muestra en el pill del header
  // y en el pie de la bandeja. Pagarlo cada 30 s en cada pestaña abierta del
  // equipo, incluidas las de supervisión que ni lo muestran, no se justifica.
  useEffect(() => {
    if (!isAuthenticated || !isAsesor) return
    const id = setInterval(() => { void refresh() }, 60_000)
    return () => clearInterval(id)
  }, [isAuthenticated, isAsesor, refresh])

  const setAvailable = useCallback(async (value: boolean) => {
    // Cambio optimista: el pill responde al toque y se revierte si el PUT
    // falla. Esperar el round-trip hace que el switch se sienta trabado.
    const previo = available
    setAvailableState(value)
    setError(null)
    try {
      await apiClient.put('/me/availability', { available: value })
      await refresh()
    } catch (err) {
      setAvailableState(previo)
      setError(err instanceof Error ? err.message : 'No se pudo cambiar la disponibilidad')
    }
  }, [available, refresh])

  const value = useMemo<MeState>(
    () => ({ available, rotationPosition, rotationSize, loading, error, setAvailable, refresh }),
    [available, rotationPosition, rotationSize, loading, error, setAvailable, refresh],
  )

  return <MeContext.Provider value={value}>{children}</MeContext.Provider>
}
